import SearchIcon from '@atlaskit/icon/glyph/search';
import SelectClearIcon from '@atlaskit/icon/glyph/select-clear';
import Textfield from '@atlaskit/textfield';
import Tooltip from '@atlaskit/tooltip';
import { useFlaggingCallback } from '@databases/flags';
import { messages } from '@databases/intl/messages';
import {
  KeyboardShortcut,
  useDebouncedModel,
  useKeyboardShortcut,
} from '@databases/react';
import { DatabaseViewSettings } from '@databases/types/database/view';
import React, { FunctionComponent, useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl-next';
import styled, { css } from 'styled-components';

import { isApple } from '../../../utils';

export interface QuickSearchProps {
  value?: DatabaseViewSettings['search'];
  onChange: (value: DatabaseViewSettings['search']) => void;
}

export const SearchFieldTransition = styled.div<{ expanded: boolean }>`
  > div {
    padding: 0 10px;
    transition: width 0.3s, background 0.3s;

    ${(props) =>
      props.expanded
        ? css`
            width: 382px;
          `
        : css`
            width: 115px;
          `}
    span {
      cursor: pointer;
      width: 20px;
      height: 20px;
      svg {
        padding-top: 2px;
      }
    }
  }
`;

const focusShortcut: KeyboardShortcut = { modifiers: ['ctrlKey'], code: 'KeyF' };
const blurShortcut: KeyboardShortcut = { code: 'Escape' };
const shortCutTooltipDelay = 1000;

const QuickSearch: FunctionComponent<QuickSearchProps> = ({
  onChange: onChangeProp,
  value: valueProp,
}) => {
  const [value, onChange] = useDebouncedModel(valueProp, onChangeProp);

  const searchFieldRef = useRef<HTMLInputElement>(null);

  const [expanded, setExpanded] = useState(!!value);

  const { formatMessage } = useIntl();

  const handleExpandField = useCallback(() => {
    searchFieldRef?.current?.focus();
    setExpanded(true);
  }, []);
  useKeyboardShortcut(expanded ? undefined : focusShortcut, handleExpandField);

  const handleBlur = useCallback(() => {
    searchFieldRef.current?.blur();
    !value && setExpanded(false);
  }, [value]);
  useKeyboardShortcut(expanded ? blurShortcut : undefined, handleBlur);

  const handleClear = useCallback(() => {
    onChange(undefined);
  }, [onChange]);

  const clearAction = useMemo(
    () =>
      expanded && (
        <div onClick={handleClear}>
          <SelectClearIcon label={formatMessage(messages['quick-search.clear-label'])} />
        </div>
      ),
    [handleClear, expanded, formatMessage]
  );

  const searchAction = useMemo(
    () => (
      <div onClick={() => (expanded ? setExpanded(false) : handleExpandField)}>
        <SearchIcon label={formatMessage(messages['quick-search.quicksearch-label'])} />
      </div>
    ),
    [expanded, formatMessage, handleExpandField]
  );

  const handleChange = useFlaggingCallback(
    (e: React.FormEvent) => {
      if (!expanded) {
        handleExpandField();
      }
      onChange((e.target as HTMLInputElement).value);
    },
    [expanded, handleExpandField, onChange]
  );

  return (
    <Tooltip
      hideTooltipOnClick
      delay={shortCutTooltipDelay}
      content={
        !expanded && (
          <>
            {formatMessage(messages['quick-search.quick-search-tooltip-text'])}{' '}
            <kbd>
              {isApple
                ? formatMessage(messages['quick-search.mac-modifier'])
                : formatMessage(messages['quick-search.ctrl'])}
            </kbd>{' '}
            {formatMessage(messages['quick-search.plus-key'])}{' '}
            <kbd>{formatMessage(messages['quick-search.F-key'])}</kbd>
          </>
        )
      }
    >
      <SearchFieldTransition expanded={expanded}>
        <Textfield
          ref={searchFieldRef}
          autoComplete="off"
          autoFocus={false}
          placeholder={formatMessage(messages['quick-search.search-database-text'])}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleExpandField}
          onClick={handleExpandField}
          value={value ?? ''}
          elemAfterInput={clearAction}
          elemBeforeInput={searchAction}
          isCompact
          testId={'database-search'}
        />
      </SearchFieldTransition>
    </Tooltip>
  );
};

export default QuickSearch;
